import { Spinner } from '@ibsys/kit';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  asChartRuleError,
  useCustomChartData,
  type CustomChartDataParams,
  type CustomChartDefinition,
} from '@/entities/dashboards';
import type { IndicatorSelectHandler } from '../../model/useIndicatorTransitions';
import { EmptyState } from '../parts/EmptyState';
import { ChartCardShell } from './ChartCardShell';
import { CustomChartView } from './CustomChartView';

/** Фильтры панели дашборда, общие для всех диаграмм пресета. */
export type CustomChartPanelFilters = Omit<CustomChartDataParams, 'chartType' | 'rule' | 'signature'>;

type Props = {
  chart: CustomChartDefinition;
  filters: CustomChartPanelFilters;
  /** Переход к диалогам выбранного элемента; не задан — содержимое переходов не выполняет */
  onSelect?: IndicatorSelectHandler;
  /** Открытие одного диалога; не задано — содержимое переходов не выполняет */
  onOpenConversation?: (conversationId: number, openInNewTab: boolean) => void;
};

/**
 * Диаграмма пользовательского дашборда: наполняется по своему правилу и общим фильтрам панели.
 *
 * Описание при показе заново не истолковывается — правило закреплено за диаграммой ещё в её
 * свойствах. Отказ по правилу показывается в самом блоке: остальные диаграммы дашборда
 * наполняются как обычно.
 *
 * Диаграмма, записанная прежним составом правила (без запроса), наполнению не подлежит: её
 * нужно пересобрать. Об этом сказано в её блоке, и к бэкенду за ней не ходят — отказ был бы
 * тем же самым, только неявным.
 *
 * Переходы к диалогам бывают погашены — карточка получает их незаданными и передаёт содержимому
 * как есть: тогда оно и не переходит, и нажимаемым не показано.
 */
export const CustomChartCard: FC<Props> = ({ chart, filters, onSelect, onOpenConversation }) => {
  const { t } = useTranslation('qualityDashboard');
  const isRuleOutdated = !chart.rule?.query;
  const { data, isLoading, error } = useCustomChartData(
    isRuleOutdated
      ? null
      : {
          chartType: chart.chartType,
          rule: chart.rule,
          signature: chart.signature || null,
          ...filters,
        },
  );

  if (isRuleOutdated) {
    return (
      <ChartCardShell title={chart.title}>
        <p className="text-sm text-muted-foreground">{t('presets.chart.outdatedRule')}</p>
      </ChartCardShell>
    );
  }

  if (isLoading) {
    return (
      <ChartCardShell title={chart.title}>
        <div className="flex justify-center py-8">
          <Spinner className="size-6" />
        </div>
      </ChartCardShell>
    );
  }

  if (error) {
    const ruleError = asChartRuleError(error);
    return (
      <ChartCardShell title={chart.title}>
        <p className="text-sm text-destructive">{ruleError?.message || t('presets.chart.loadFailed')}</p>
      </ChartCardShell>
    );
  }

  if (!data || data.total === 0) {
    return (
      <ChartCardShell title={chart.title}>
        <EmptyState />
      </ChartCardShell>
    );
  }

  return (
    <CustomChartView
      title={chart.title}
      data={data}
      hasFixedHeight={chart.height !== undefined}
      onSelect={onSelect}
      onOpenConversation={onOpenConversation}
    />
  );
};
